import { useEffect, useState } from "react";
import InputField from "./InputField";

const AutocompleteField = ({
  field,
  register,
  watch,
  validationRules,
  errors,
  apiError,
  setValue,
}: any) => {
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const dependencyValues = watch(field.dependsOn || []);
  const currentValue = watch(field.name) || "";

  useEffect(() => {
    const fetchTrigger = async () => {
      // If field has static options, use them directly
      if (field.options) {
        setSuggestions(field.options || []);
        return;
      }

      // Otherwise, use fetchOptions if dependencies are met
      const allDepsFilled = field.dependsOn?.every(
        (_: any, index: number) => !!dependencyValues[index],
      );

      if (allDepsFilled) {
        try {
          const data = await field.fetchOptions?.(watch());
          setSuggestions(data || []);
        } catch (err) {
          console.error(`Error fetching suggestions for ${field.name}:`, err);
        }
      } else {
        setSuggestions([]);
      }
    };
    fetchTrigger();
  }, [JSON.stringify(dependencyValues), field.name, field.options]);

  const filteredSuggestions = suggestions.filter((opt) => {
    const val = typeof opt === "string" ? opt : opt.variant;
    return val?.toLowerCase().includes(currentValue.toLowerCase());
  });

  return (
    <div className="relative w-full">
      <InputField
        type="text"
        placeholder={field.placeholder}
        {...register(field.name, validationRules)}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setTimeout(() => setIsOpen(false), 200)}
        autoComplete="off"
      />

      {isOpen && filteredSuggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
          {filteredSuggestions.map((opt: any, i: number) => {
            const isObject = typeof opt === "object" && opt !== null;
            const mainVal = isObject ? opt.variant : opt;
            const subVal = isObject ? opt.year_of_manufacture : null;

            return (
              <div
                key={i}
                className="px-4 py-2 hover:bg-blue-50 cursor-pointer flex justify-between items-center transition-colors border-b last:border-0 border-gray-50"
                onMouseDown={() => {
                  // Set the variant name
                  setValue(field.name, mainVal, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });

                  // Auto-fill Year if it exists in the selected object
                  if (subVal) {
                    setValue("year_of_manufacture", subVal, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }

                  setIsOpen(false);
                }}
              >
                <span className="text-sm text-gray-700 font-medium">
                  {mainVal}
                </span>
                {subVal && (
                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                    {subVal}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p className="mt-1 text-sm text-red-600">{errors[field.name]?.message}</p>
      <p className="mt-1 text-sm text-red-600">{apiError}</p>
    </div>
  );
};
export default AutocompleteField;
